import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  matCalendarMonthSharp,
  matCommentSharp,
  matDeleteSharp,
  matMedicationSharp,
  matPetsSharp,
  matScaleSharp,
} from '@ng-icons/material-icons/sharp';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { WeightEntry } from '../../../../services/apiclient/WeightEntry';
import { ApiClientService } from '../../../../services/apiclient/api-client.service';
import { gameSightDisabled } from '@ng-icons/game-icons';

@Component({
  selector: 'app-weight-entry-card',
  imports: [
    DatePipe,
    TooltipModule,
    ButtonModule,
    NgIconComponent,
    CardModule,
    ConfirmDialogModule,
  ],
  providers: [
    provideIcons({
      matScaleSharp,
      matCalendarMonthSharp,
      matPetsSharp,
      matCommentSharp,
      matMedicationSharp,
      matDeleteSharp,
      gameSightDisabled,
    }),
    ConfirmationService,
  ],
  templateUrl: './weight-entry-card.component.html',
  styleUrl: './weight-entry-card.component.css',
})
export class WeightEntryCardComponent {
  @Input({ required: true }) weightEntry!: WeightEntry;

  @Output() entryDeleted = new EventEmitter<WeightEntry>();

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiClientService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  getMedicineAbbreviationList(): string {
    return this.weightEntry.medicineObjects
      .map((x) => x.abbreviation)
      .sort()
      .join(', ');
  }

  async navigateToEdit(): Promise<void> {
    await this.router.navigate(['edit', this.weightEntry.id], {
      relativeTo: this.route,
    });
  }

  confirmDeleteEntry(): void {
    const weighedAt = new Date(
      Date.parse(this.weightEntry.weighedAt),
    ).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    this.confirmationService.confirm({
      closeOnEscape: true,
      dismissableMask: true,
      header: 'Bist du sicher?',
      message: `Möchtest du diesen Eintrag (${weighedAt} für ${this.weightEntry.rabbit.name}) wirklich löschen?`,
      acceptButtonProps: {
        label: 'Löschen',
        severity: 'danger',
      },
      rejectButtonProps: {
        label: 'Abbrechen',
        outlined: true,
      },
      accept: () => this.deleteEntry(),
    });
  }

  deleteEntry(): void {
    this.api.deleteWeightEntryById(this.weightEntry.id).subscribe({
      next: (_) => this.entryDeleted.emit(this.weightEntry),
      error: (error: HttpErrorResponse) => {
        switch (error.status) {
          case 404:
            this.messageService.add({
              severity: 'warning',
              summary: 'Nicht gefunden',
              detail: 'Der Eintrag existiert auf dem Server nicht mehr.',
            });
            break;
          case 500:
            this.messageService.add({
              severity: 'warning',
              summary: 'Serverfehler',
              detail:
                'Beim Löschen des Eintrags ist ein Fehler aufgetreten. Bitte versuche es erneut.',
            });
            break;
          default:
            break;
        }
      },
    });
  }
}
